<script setup lang="ts">
// 사람·조직·관계 관리(design/manage.js 포팅). 모든 변경은 즉시 암호화 금고에 저장된다.
// 미포함(다음 증분): undo, AI 관계 제안, '나' 조직 배치(지도 포팅 때).
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVaultStore, type Relation } from '../../stores/vault'
import type { Person } from '../../data/demo'

const { t } = useI18n()
const vault = useVaultStore()
const props = defineProps<{ focusId?: string }>()
const emit = defineEmits<{ toast: [msg: string] }>()

type Tab = 'people' | 'org' | 'rel'
const tab = ref<Tab>('people')
const openId = ref<string | null>(null)
// 밖에서 특정 사람으로 열기(일기 새 사람 포착 → 조직 위치 지정)
watch(() => props.focusId, (id) => { if (id) { tab.value = 'people'; openId.value = id } }, { immediate: true })
const q = ref('')
const delArm = ref<string | null>(null) // 삭제 2단계 확인

const PALETTE = [['#FFB86B', '#FF6B4A'], ['#7EE0B5', '#2FA3C7'], ['#B9A6FF', '#6B5BD6'], ['#FF9EC4', '#E0548F'],
  ['#FFD36B', '#E59A1C'], ['#C4F07A', '#6CBF3A'], ['#8FD3FF', '#3F7FE0'], ['#F6A6A6', '#D9534F']]
const TITLE_LV: Record<string, number> = { 사원: 5, 인턴: 5, 주임: 4, 대리: 4, 선임: 4, 과장: 3, 차장: 3, 책임: 3, 부장: 2, 팀장: 2, 실장: 2, 파트장: 2, 본부장: 1, 이사: 1, 상무: 1, 전무: 1, 부사장: 1, 사장: 1, 대표: 1 }
const TITLES = Object.keys(TITLE_LV).sort((a, b) => b.length - a.length)
const LEVELS = [1, 2, 3, 4, 5]
const REL_TYPES: Relation['type'][] = ['friend', 'work', 'tension']

const lvName = (n?: number) => t(`manage.lv${n || 4}`)
const typeName = (ty: Relation['type']) => t(`manage.type${ty[0].toUpperCase()}${ty.slice(1)}`)
const deptName = (id?: string | null) => vault.depts.find(d => d.id === id)?.name || t('manage.deptNone')
const nameOf = (id: string) => (id === 'me' ? t('manage.me') : vault.people.find(p => p.id === id)?.name || '?')

function saved() { emit('toast', t('manage.saved')) }

/* ── 한 줄 추가 파서(규칙 기반; 실제 앱에선 AI가 같은 미리보기를 보여 준다) ── */
interface Parsed { name: string; title: string; level: number; dept: string | null; deptId: string | null; newDept: boolean; exists: boolean }
const parsed = computed<Parsed | null>(() => {
  const text = q.value.trim()
  if (!text) return null
  const tokens = text.split(/[\s,]+/).filter(Boolean)
  const used = new Set<number>()
  let dept: string | null = null, title: string | null = null, name: string | null = null
  const bm = text.match(/(\S+?)\s*(?:님)?\s*(?:밑|아래|산하)/)
  if (bm) {
    const who = bm[1].replace(/[,님]$/, '')
    tokens.forEach((k, i) => { if (k.startsWith(who) || /^(밑|아래|산하)$/.test(k)) used.add(i) })
  }
  tokens.forEach((k, i) => {
    if (used.has(i)) return
    if (/(팀|실|본부|센터|그룹|부)$/.test(k) && !/장$/.test(k) && k.length >= 2 && !TITLE_LV[k]) { if (!dept) { dept = k; used.add(i) } }
    else if (TITLE_LV[k]) { if (!title) { title = k; used.add(i) } }
  })
  tokens.forEach((k, i) => { if (!used.has(i) && !name) { name = k; used.add(i) } })
  if (!name) return null
  if (!title) { const suf = TITLES.find(x => name!.endsWith(x) && name!.length > x.length); if (suf) title = suf }
  const deptHit = dept ? vault.depts.find(x => x.name === dept) : null
  return {
    name, title: title || '', level: title ? TITLE_LV[title] : 4,
    dept, deptId: deptHit?.id ?? null, newDept: !!(dept && !deptHit),
    exists: vault.people.some(p => p.name === name),
  }
})

async function addPerson(opts: { name: string; title?: string; level?: number; deptId?: string | null; newDept?: boolean; dept?: string | null }): Promise<Person> {
  let deptId = opts.deptId ?? null
  if (opts.newDept && opts.dept) { deptId = crypto.randomUUID(); await vault.saveDept({ id: deptId, name: opts.dept }) }
  const k = vault.people.length % PALETTE.length
  const p: Person = {
    id: crypto.randomUUID(), name: opts.name, title: opts.title || '', rel: '', level: opts.level || 4,
    dept: deptId, close: 0.3, c1: PALETTE[k][0], c2: PALETTE[k][1], aliases: [],
    tip: '아직 기록이 없어요. 한 줄씩 남겨 보세요.', dos: [], donts: [],
  }
  await vault.savePerson(p)
  return p
}

async function addQuick() {
  if (!parsed.value || parsed.value.exists) return
  const p = await addPerson(parsed.value)
  q.value = ''; openId.value = p.id; tab.value = 'people'
  emit('toast', `${p.name} 추가됨`)
}

/* ── 사람 편집 ── */
async function edit(p: Person, field: keyof Person, value: unknown) {
  if (field === 'name') { const v = String(value).trim(); if (!v) return; p.name = v }
  else if (field === 'aliases') p.aliases = String(value).split(',').map(s => s.trim()).filter(Boolean)
  else if (field === 'close') p.close = Number(value) / 100
  else if (field === 'dept') p.dept = (value as string) || null
  else if (field === 'company') { p.company = (value as string) || null; if (p.company) p.dept = null } // 외부 조직이면 내 회사 부서는 비운다
  else (p as any)[field] = value
  await vault.savePerson(p); saved()
}

/* ── 고객사(외부 조직) ── */
const cliMembers = (cid: string) => vault.people.filter(p => p.company === cid).sort((a, b) => (a.level || 4) - (b.level || 4))
async function renameCompany(id: string, name: string) {
  const c = vault.companies.find(x => x.id === id); if (!c || !name.trim() || name.trim() === c.name) return
  c.name = name.trim(); await vault.saveCompany(c); saved()
}
async function addCompany() {
  await vault.saveCompany({ id: crypto.randomUUID(), name: t('manage.newCompanyName') }); emit('toast', t('manage.saved'))
}
async function removePerson(id: string) {
  if (delArm.value !== id) { delArm.value = id; return }
  const name = vault.people.find(p => p.id === id)?.name
  await vault.deletePerson(id); openId.value = null; delArm.value = null
  emit('toast', `${name} 삭제됨`)
}

/* ── 부서 ── */
async function renameDept(id: string, name: string) {
  const d = vault.depts.find(x => x.id === id); if (!d || !name.trim() || name.trim() === d.name) return
  d.name = name.trim(); await vault.saveDept(d); saved()
}
async function addDept() {
  await vault.saveDept({ id: crypto.randomUUID(), name: t('manage.newDeptName') }); emit('toast', t('manage.saved'))
}
const addToDeptText = ref<Record<string, string>>({})
async function addToDept(deptId: string) {
  const txt = (addToDeptText.value[deptId] || '').trim(); if (!txt) return
  const pr = parsed.value && q.value === txt ? parsed.value : null
  const name = pr?.name || txt
  if (vault.people.some(p => p.name === name)) { emit('toast', t('manage.nameExists')); return }
  const p = await addPerson({ name, title: pr?.title, level: pr?.level, deptId })
  addToDeptText.value[deptId] = ''
  emit('toast', `${p.name} 추가됨`)
}

/* ── 관계 ── */
const peopleNoDept = computed(() => vault.people.filter(p => !p.dept || !vault.depts.some(d => d.id === p.dept)).sort((a, b) => (a.level || 4) - (b.level || 4)))
const membersOf = (deptId: string) => vault.people.filter(p => p.dept === deptId).sort((a, b) => (a.level || 4) - (b.level || 4))

const newRel = ref({ a: '', b: '', type: 'friend' as Relation['type'], note: '' })
async function addRel() {
  const { a, b, type, note } = newRel.value
  if (!a || !b || a === b) { emit('toast', t('manage.pickTwo')); return }
  if (vault.relations.some(r => (r.a === a && r.b === b) || (r.a === b && r.b === a))) { emit('toast', t('manage.relExists')); return }
  await vault.addRelation(a, b, type, note.trim()); newRel.value.note = ''
  emit('toast', t('manage.saved'))
}
async function setRelType(r: Relation, ty: Relation['type']) { r.type = ty; await vault.saveRelation(r); saved() }
async function setRelNote(r: Relation, note: string) { r.note = note; await vault.saveRelation(r); saved() }

const relPeople = computed(() => [{ id: 'me', name: t('manage.me') }, ...vault.people.map(p => ({ id: p.id, name: p.name }))])
</script>

<template>
  <div class="mg">
    <div class="mg-tabs" role="tablist">
      <button :class="{ sel: tab === 'people' }" @click="tab = 'people'; openId = null">{{ t('manage.tabPeople') }}<em>{{ vault.people.length }}</em></button>
      <button :class="{ sel: tab === 'org' }" @click="tab = 'org'; openId = null">{{ t('manage.tabOrg') }}</button>
      <button :class="{ sel: tab === 'rel' }" @click="tab = 'rel'; openId = null">{{ t('manage.tabRel') }}<em>{{ vault.relations.length }}</em></button>
    </div>

    <!-- 한 줄 추가 (사람·조직 탭) -->
    <div v-if="tab !== 'rel'" class="mg-quick">
      <label>{{ t('manage.quickLabel') }}</label>
      <div class="row">
        <input v-model="q" autocomplete="off" :placeholder="t('manage.quickPlaceholder')" @keydown.enter="addQuick" />
        <button class="mg-btn" :disabled="!parsed || parsed.exists" @click="addQuick">{{ t('manage.add') }}</button>
      </div>
      <div v-if="parsed" class="mg-preview">
        <span v-if="parsed.exists" class="mg-chip">{{ t('manage.nameExists') }}: <b>{{ parsed.name }}</b></span>
        <template v-else>
          <span class="mg-chip">{{ t('manage.fName') }} <b>{{ parsed.name }}</b></span>
          <span class="mg-chip" :class="{ new: parsed.newDept }">{{ parsed.dept || t('manage.deptNone') }}</span>
          <span class="mg-chip"><b v-if="parsed.title">{{ parsed.title }}</b> {{ lvName(parsed.level) }}</span>
        </template>
      </div>
    </div>

    <!-- 사람 -->
    <div v-if="tab === 'people'">
      <div class="mg-list">
        <div v-for="p in vault.people" :key="p.id" class="mg-item" :class="{ open: openId === p.id }">
          <button class="mg-row" @click="openId = openId === p.id ? null : p.id; delArm = null">
            <span class="mg-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></span>
            <span class="tx"><b>{{ p.name }}</b><span>{{ deptName(p.dept) }} · {{ p.title || lvName(p.level) }}</span></span>
            <span class="chev">›</span>
          </button>
          <div v-if="openId === p.id" class="mg-edit">
            <div class="mg-f two">
              <label><span>{{ t('manage.fName') }}</span><input :value="p.name" @change="edit(p, 'name', ($event.target as HTMLInputElement).value)" /></label>
              <label><span>{{ t('manage.fTitle') }}</span><input :value="p.title" :placeholder="t('manage.fTitlePlaceholder')" @change="edit(p, 'title', ($event.target as HTMLInputElement).value)" /></label>
            </div>
            <label class="mg-f"><span>{{ t('manage.fAliases') }}</span><input :value="(p.aliases || []).join(', ')" :placeholder="t('manage.fAliasesPlaceholder')" @change="edit(p, 'aliases', ($event.target as HTMLInputElement).value)" /></label>
            <label class="mg-f"><span>{{ t('manage.fCompany') }}</span>
              <select :value="p.company || ''" @change="edit(p, 'company', ($event.target as HTMLSelectElement).value)">
                <option value="">{{ t('manage.myCompany') }}</option>
                <option v-for="c in vault.companies" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </label>
            <div class="mg-f two">
              <label><span>{{ t('manage.fDept') }}</span>
                <select :value="p.dept || ''" @change="edit(p, 'dept', ($event.target as HTMLSelectElement).value)">
                  <option value="">{{ t('manage.deptNone') }}</option>
                  <option v-for="d in vault.depts" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
              </label>
              <label><span>{{ t('manage.fBoss') }}</span>
                <select :value="p.boss || ''" @change="edit(p, 'boss', ($event.target as HTMLSelectElement).value || undefined)">
                  <option value="">{{ t('manage.bossNone') }}</option>
                  <option value="me">{{ t('manage.me') }}</option>
                  <option v-for="x in vault.people.filter(x => x.id !== p.id)" :key="x.id" :value="x.id">{{ x.name }}</option>
                </select>
              </label>
            </div>
            <div class="mg-f"><span>{{ t('manage.fLevel') }}</span>
              <div class="mg-lv"><button v-for="lv in LEVELS" :key="lv" :class="{ sel: (p.level || 4) === lv }" @click="edit(p, 'level', lv)">{{ lvName(lv) }}</button></div>
            </div>
            <div class="mg-f"><span>{{ t('manage.fClose') }}</span>
              <div class="mg-range">{{ t('manage.far') }}<input type="range" min="0" max="100" :value="Math.round((p.close || 0) * 100)" @change="edit(p, 'close', ($event.target as HTMLInputElement).value)" />{{ t('manage.near') }}</div>
            </div>
            <div class="mg-foot"><span></span><button class="mg-btn danger" @click="removePerson(p.id)">{{ delArm === p.id ? t('manage.delSure') : t('manage.del') }}</button></div>
          </div>
        </div>
      </div>
      <p class="mg-hint">{{ t('manage.peopleHint') }}</p>
    </div>

    <!-- 조직 -->
    <div v-else-if="tab === 'org'">
      <div v-if="peopleNoDept.length" class="mg-dept">
        <div class="mg-dept-h"><b style="padding-left:6px">{{ t('manage.orgExec') }}</b><small>{{ t('manage.memberN', { n: peopleNoDept.length }) }}</small></div>
        <div v-for="p in peopleNoDept" :key="p.id" class="mg-org-row">
          <span class="mg-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></span>
          <span class="tx">{{ p.name }}<span>{{ p.title }}</span></span>
          <span class="mg-lvtag">{{ lvName(p.level) }}</span>
          <select :value="p.dept || ''" @change="edit(p, 'dept', ($event.target as HTMLSelectElement).value)">
            <option value="">{{ t('manage.deptNone') }}</option>
            <option v-for="d in vault.depts" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>
      </div>
      <div v-for="d in vault.depts" :key="d.id" class="mg-dept">
        <div class="mg-dept-h">
          <input :value="d.name" @change="renameDept(d.id, ($event.target as HTMLInputElement).value)" />
          <small>{{ t('manage.memberN', { n: membersOf(d.id).length }) }}</small>
          <button v-if="!membersOf(d.id).length" class="mg-btn ghost" @click="vault.deleteDept(d.id)">{{ t('manage.del') }}</button>
        </div>
        <div v-for="p in membersOf(d.id)" :key="p.id" class="mg-org-row">
          <span class="mg-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></span>
          <span class="tx">{{ p.name }}<span>{{ p.title }}</span></span>
          <span class="mg-lvtag">{{ lvName(p.level) }}</span>
          <select :value="p.dept || ''" @change="edit(p, 'dept', ($event.target as HTMLSelectElement).value)">
            <option value="">{{ t('manage.deptNone') }}</option>
            <option v-for="x in vault.depts" :key="x.id" :value="x.id">{{ x.name }}</option>
          </select>
        </div>
        <div class="mg-add-in"><input v-model="addToDeptText[d.id]" :placeholder="t('manage.addToDept')" autocomplete="off" @keydown.enter="addToDept(d.id)" /></div>
      </div>
      <button class="mg-btn ghost" style="width:100%" @click="addDept">{{ t('manage.addDept') }}</button>
      <p class="mg-hint">{{ t('manage.orgHint') }}</p>

      <!-- 고객사(외부 조직) — 내 회사와 구분, 지도에서 다른 색/영역 -->
      <div class="mg-sec" style="margin-top:22px">{{ t('manage.companiesTitle') }}</div>
      <div v-for="c in vault.companies" :key="c.id" class="mg-dept ext">
        <div class="mg-dept-h">
          <input :value="c.name" @change="renameCompany(c.id, ($event.target as HTMLInputElement).value)" />
          <small>{{ t('manage.memberN', { n: cliMembers(c.id).length }) }}</small>
          <button v-if="!cliMembers(c.id).length" class="mg-btn ghost" @click="vault.deleteCompany(c.id)">{{ t('manage.del') }}</button>
        </div>
        <div v-for="p in cliMembers(c.id)" :key="p.id" class="mg-org-row">
          <span class="mg-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></span>
          <span class="tx">{{ p.name }}<span>{{ p.title }}</span></span>
          <span class="mg-lvtag">{{ lvName(p.level) }}</span>
          <select :value="p.company || ''" @change="edit(p, 'company', ($event.target as HTMLSelectElement).value)">
            <option value="">{{ t('manage.myCompany') }}</option>
            <option v-for="x in vault.companies" :key="x.id" :value="x.id">{{ x.name }}</option>
          </select>
        </div>
      </div>
      <button class="mg-btn ghost" style="width:100%" @click="addCompany">{{ t('manage.addCompany') }}</button>
    </div>

    <!-- 관계 -->
    <div v-else>
      <div class="mg-sec">{{ t('manage.relConfirmed') }}</div>
      <div class="mg-list">
        <div v-for="r in vault.relations" :key="r.id" class="mg-rel">
          <div class="mg-rel-h"><b>{{ nameOf(r.a) }}</b> ↔ <b>{{ nameOf(r.b) }}</b><button class="x" @click="vault.deleteRelation(r.id)" aria-label="삭제">×</button></div>
          <div class="mg-type"><button v-for="ty in REL_TYPES" :key="ty" :data-t="ty" :class="{ sel: r.type === ty }" @click="setRelType(r, ty)">{{ typeName(ty) }}</button></div>
          <input :value="r.note" :placeholder="t('manage.relMemoPlaceholder')" @change="setRelNote(r, ($event.target as HTMLInputElement).value)" />
        </div>
        <p v-if="!vault.relations.length" class="mg-hint">{{ t('manage.relNone') }}</p>
      </div>
      <div class="mg-newrel">
        <b style="font-size:14px">{{ t('manage.relAddTitle') }}</b>
        <div class="pair">
          <select v-model="newRel.a"><option v-for="p in relPeople" :key="p.id" :value="p.id">{{ p.name }}</option></select>
          <span>↔</span>
          <select v-model="newRel.b"><option v-for="p in relPeople" :key="p.id" :value="p.id">{{ p.name }}</option></select>
        </div>
        <div class="mg-type"><button v-for="ty in REL_TYPES" :key="ty" :data-t="ty" :class="{ sel: newRel.type === ty }" @click="newRel.type = ty">{{ typeName(ty) }}</button></div>
        <input v-model="newRel.note" :placeholder="t('manage.relMemoPlaceholder')" />
        <button class="mg-btn" @click="addRel">{{ t('manage.add') }}</button>
      </div>
    </div>
  </div>
</template>
